import PageHeader from "@/components/pageHeader";
import { Award, Calendar } from "lucide-react";
import { useEffect, useRef, useState } from "react";
export const Awards = () => {
  const allRecognitions = [
    { year: "2022", title: "Asia One India's Greatest Brand", desc: "Honoured by Asia-One Magazine as one of India's Greatest Brands.", pub: "ASIA ONE", pubStyle: "sans" },
    { year: "2022", title: "Most Influential Leaders 2021-22", desc: "Mr. Tejpreet Singh Gill honoured by Asia-One Magazine in the 40 Under 40 Most Influential Leaders category.", pub: "ASIA ONE", pubStyle: "sans" },
    { year: "2022", title: "Forbes India Celebrity 100", desc: "Tejpreet Gill recognized on the prestigious Forbes India Celebrity 100 for his extraordinary leadership and unparalleled contributions to real estate.", pub: "Forbes", pubStyle: "forbes" },
    { year: "2022", title: "India Today: Icons of India", desc: "Recognized and felicitated as the face of the region's most successful real estate group, further solidifying Gillco's legacy of excellence.", pub: "INDIA TODAY", pubStyle: "sans-bold" },
    { year: "2022", title: "MY FM Excellence Awards", desc: "Awarded the MY FM Young Entrepreneur Real Estate Award, celebrating our commitment to innovation and growth.", pub: "MY FM", pubStyle: "myfm" },
    { year: "2022", title: "Forbes Limelight - Tycoon of Tomorrow", desc: "Featured in Forbes' December issue as a 'Tycoon of Tomorrow', marking a golden milestone in Gillco Group's legacy.", pub: "Forbes", pubStyle: "forbes" },
    { year: "2010", title: "Hindustan Times Real Estate Developer of the Year", desc: "Recognized by Hindustan Times as a leading real estate developer for the fifth consecutive year.", pub: "Hindustan Times", pubStyle: "ht" },
    { year: "2009", title: "Hindustan Times Real Estate Developer of the Year", desc: "Awarded by Hindustan Times for excellence in real estate development.", pub: "Hindustan Times", pubStyle: "ht" },
    { year: "2008", title: "Hindustan Times Real Estate Developer of the Year", desc: "Recognized by Hindustan Times for sustained leadership in the real estate sector.", pub: "Hindustan Times", pubStyle: "ht" },
    { year: "2007", title: "Hindustan Times Real Estate Developer of the Year", desc: "Awarded by Hindustan Times for innovation and excellence in real estate.", pub: "Hindustan Times", pubStyle: "ht" },
    { year: "2006", title: "Hindustan Times Real Estate Developer of the Year", desc: "First recognition by Hindustan Times as a leading real estate developer.", pub: "Hindustan Times", pubStyle: "ht" },
  ];

  return (
    <div data-testid="v13-awards-page">
      <PageHeader title="Awards & Recognitions" subtitle="Our Achievements" backgroundImage="/assets/award/award.jpeg" />

      {/* Unified Hero + Timeline Section — Milestones-style layout */}
      <section className="bg-white pt-24 lg:pt-32 pb-24 lg:pb-32">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          {/* Hero statement */}
          <div className="flex gap-8 lg:gap-16 mb-20 lg:mb-28">
            {/* Vertical rotated label + gold line */}
            <div className="hidden md:flex flex-col items-center">
              <span
                className="text-[11px] tracking-[0.3em] uppercase text-[#C4A265]"
                style={{ writingMode: "vertical-lr", letterSpacing: "0.3em" }}
              >
                RECOGNITIONS
              </span>
              <div className="w-[1px] bg-[#C4A265] flex-1 mt-4 min-h-[120px]" />
            </div>

            {/* Heading + Body */}
            <div className="flex-1">
              <h2
                className="text-4xl sm:text-5xl lg:text-6xl text-[#0A0A0A] mb-12 lg:mb-16 leading-[1.05] relative inline-block pr-6"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                A Legacy of<br />Honours &amp; Distinctions
                {/* Gold dot accent */}
                <span className="absolute bottom-3 -right-2 w-2 h-2 rounded-full bg-[#C4A265]" />
              </h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-6" style={{ textAlign: "justify" }}>
                For over four decades, Gillco Group has been honoured with some of the most prestigious accolades from leading institutions across India. Each recognition is more than an award — it is a testament to the resilience, foresight and unwavering pursuit of excellence that have come to define our craft in real estate.
              </p>
              <p className="text-[#4A4A4A] leading-relaxed" style={{ textAlign: "justify" }}>
                From Forbes and Hindustan Times to Asia One, India Today and MY FM, these honours mark moments of validation along a journey shaped by purpose. They reflect not just what we have built, but how — through enduring values, visionary leadership and a deep commitment to communities and the families who call Gillco home.
              </p>
            </div>
          </div>

          {/* Timeline list */}
          <div className="relative">
            {/* Vertical gold gradient line */}
            <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-[#C4A265]/40 to-transparent hidden md:block" />
            <div className="space-y-0">
              {allRecognitions.map((a, i) => (
                <AwardTimelineItem key={i} award={a} index={i} total={allRecognitions.length} />
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
// Publication wordmark renderer — styled distinctively per brand (grayscale, editorial)
const PublicationWordmark = ({ name, variant }) => {
  const baseClass = "inline-block transition-all duration-500";
  switch (variant) {
    case "forbes":
      // Forbes — heavy slab/serif italic-ish (using bold serif italic)
      return (
        <span
          className={`${baseClass} text-2xl lg:text-[26px] text-[#1a1a1a]/70 group-hover:text-[#1a1a1a] tracking-tight`}
          style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif", fontWeight: 700, fontStyle: "italic", letterSpacing: "-0.02em" }}
        >
          {name}
        </span>
      );
    case "ht":
      // Hindustan Times — refined serif wordmark, all caps light tracked
      return (
        <span
          className={`${baseClass} text-[13px] lg:text-sm text-[#1a1a1a]/70 group-hover:text-[#1a1a1a] uppercase`}
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, letterSpacing: "0.18em" }}
        >
          {name}
        </span>
      );
    case "sans-bold":
      // India Today — bold sans uppercase
      return (
        <span
          className={`${baseClass} text-[13px] lg:text-sm text-[#1a1a1a]/70 group-hover:text-[#1a1a1a] uppercase`}
          style={{ fontFamily: "Helvetica, Arial, sans-serif", fontWeight: 900, letterSpacing: "0.05em" }}
        >
          {name}
        </span>
      );
    case "myfm":
      // MY FM — circular badge style
      return (
        <span className={`${baseClass} inline-flex items-center gap-1.5`}>
          <span
            className="inline-flex items-center justify-center w-7 h-7 rounded-full border-[1.5px] border-[#1a1a1a]/60 group-hover:border-[#1a1a1a] text-[10px] font-bold text-[#1a1a1a]/70 group-hover:text-[#1a1a1a]"
            style={{ fontFamily: "Helvetica, Arial, sans-serif", letterSpacing: "0" }}
          >
            MY
          </span>
          <span
            className="text-[13px] font-bold text-[#1a1a1a]/70 group-hover:text-[#1a1a1a]"
            style={{ fontFamily: "Helvetica, Arial, sans-serif", letterSpacing: "0.1em" }}
          >
            FM
          </span>
        </span>
      );
    case "sans":
    default:
      // Asia One — clean sans uppercase medium
      return (
        <span
          className={`${baseClass} text-[13px] lg:text-sm text-[#1a1a1a]/70 group-hover:text-[#1a1a1a] uppercase`}
          style={{ fontFamily: "Helvetica, Arial, sans-serif", fontWeight: 700, letterSpacing: "0.22em" }}
        >
          {name}
        </span>
      );
  }
};

// Vertical Timeline-Style Row with publication wordmark
const AwardTimelineItem = ({ award, index, total }) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.unobserve(el); } }, { threshold: 0.25 });
    obs.observe(el); return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      data-testid={`v13-award-timeline-${index}`}
      className="relative md:pl-12 py-8 lg:py-10 group border-b border-[#C4A265]/15 last:border-b-0"
      style={{
        opacity: vis ? 1 : 0,
        transform: vis ? "translateX(0)" : "translateX(-30px)",
        transition: `all 0.8s cubic-bezier(0.16,1,0.3,1) ${index * 0.06}s`,
      }}
    >
      {/* Gold dot on timeline */}
      <div className="absolute left-[-5px] top-12 w-3 h-3 rounded-full bg-white border-2 border-[#C4A265] group-hover:bg-[#C4A265] group-hover:scale-125 transition-all duration-500 hidden md:block" />

      <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-start">
        {/* Index number */}
        <div className="md:col-span-1 hidden md:block">
          <span
            className="text-xl text-[#C4A265]/50 font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>

        {/* Publication wordmark + Year + Title */}
        <div className="md:col-span-5">
          <div className="mb-4 h-9 flex items-center" data-testid={`v13-award-pub-${index}`}>
            <PublicationWordmark name={award.pub} variant={award.pubStyle} />
          </div>
          <div className="flex items-center gap-3 mb-3">
            <Calendar size={13} className="text-[#C4A265]" />
            <span className="font-body text-[11px] tracking-[0.3em] uppercase text-[#C4A265]">{award.year}</span>
          </div>
          <h4
            className="text-xl lg:text-2xl text-[#0A0A0A] font-light leading-[1.25] group-hover:text-[#0A4F3B] transition-colors duration-500"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {award.title}
          </h4>
        </div>

        {/* Description */}
        <div className="md:col-span-6">
          <p className="text-sm lg:text-[15px] text-[#4A4A4A] leading-relaxed">{award.desc}</p>
        </div>
      </div>
    </div>
  );
};